import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ArrowRight,
  Phone,
  Calendar,
  MessageCircle,
  CheckCircle,
  Clock,
  Shield,
} from 'lucide-react';

interface MontanaCTAProps {
  className?: string;
  onScheduleDemo?: () => void;
  onContactSales?: () => void;
  onGetStarted?: () => void;
}

export const MontanaCTA: React.FC<MontanaCTAProps> = ({
  className = '',
  onScheduleDemo,
  onContactSales,
  onGetStarted,
}) => {
  return (
    <div
      className={`py-20 bg-linear-to-br from-blue-600 via-blue-700 to-indigo-800 text-white ${className}`}
    >
      <div className="container mx-auto px-4">
        {/* Main CTA Section */}
        <div className="text-center mb-16">
          <Badge className="mb-6 bg-white/20 text-white border-white/30 text-lg px-4 py-2">
            <CheckCircle className="w-5 h-5 mr-2" />
            Ready to Transform Your Community?
          </Badge>

          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            Start Your Digital
            <span className="block text-blue-200">Transformation Today</span>
          </h2>

          <p className="text-xl md:text-2xl text-white/90 max-w-4xl mx-auto mb-8">
            Join forward-thinking Montana communities who've modernized their operations with
            transparent governance, efficient management, and engaged residents.
          </p>

          {/* Primary CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button
              size="lg"
              className="bg-white text-blue-700 hover:bg-blue-50 font-semibold text-lg px-8 py-4"
              onClick={onGetStarted}
            >
              Get Started Now
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>

            <Button
              size="lg"
              className="bg-white/20 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-blue-700 font-semibold text-lg px-8 py-4"
              onClick={onScheduleDemo}
            >
              <Calendar className="w-5 h-5 mr-2" />
              Schedule Demo
            </Button>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center gap-6 text-blue-200 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              <span>No long-term contracts</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span>Professional setup & training</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>Implementation timeline varies by scope</span>
            </div>
          </div>
        </div>

        {/* Contact Options */}
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-16">
          {/* Schedule Demo */}
          <Card className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/15 transition-all duration-300">
            <CardContent className="p-6 text-center">
              <div className="bg-white/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Schedule a Demo</h3>
              <p className="text-white/80 mb-4 text-sm">
                See the platform in action with your community's data
              </p>
              <Button
                className="bg-white text-blue-700 hover:bg-blue-50 hover:text-blue-800 w-full font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.01] transition-all duration-200 focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-blue-700"
                onClick={onScheduleDemo}
              >
                Book 30-Min Demo
              </Button>
            </CardContent>
          </Card>

          {/* Talk to Sales */}
          <Card className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/15 transition-all duration-300">
            <CardContent className="p-6 text-center">
              <div className="bg-white/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Talk to Sales</h3>
              <p className="text-white/80 mb-4 text-sm">
                Get a custom quote and implementation plan
              </p>
              <Button
                className="bg-white/10 backdrop-blur-sm border-2 border-white/60 text-white hover:bg-white/20 hover:border-white/80 w-full font-medium shadow-md hover:shadow-lg transition-all duration-200 focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-blue-800"
                onClick={onContactSales}
              >
                Contact Sales
              </Button>
            </CardContent>
          </Card>

          {/* Get Support */}
          <Card className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/15 transition-all duration-300">
            <CardContent className="p-6 text-center">
              <div className="bg-white/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Ask Questions</h3>
              <p className="text-white/80 mb-4 text-sm">
                Get answers about features, pricing, or implementation
              </p>
              <Button className="bg-transparent border border-white/40 text-white/90 hover:bg-white/10 hover:text-white hover:border-white/60 w-full font-medium transition-all duration-200 focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-offset-2 focus-visible:ring-offset-blue-900">
                Chat with Us
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Implementation Promise */}
        <Card className="bg-white/10 backdrop-blur-sm border-white/20 max-w-4xl mx-auto">
          <CardContent className="p-8">
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-4 text-white">Our Implementation Promise</h3>
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-3xl font-bold text-blue-200 mb-2">Phase 1</div>
                  <div className="text-sm text-blue-100">Setup & Configuration</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-blue-200 mb-2">Phase 2</div>
                  <div className="text-sm text-blue-100">Data Migration & Training</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-blue-200 mb-2">Phase 3</div>
                  <div className="text-sm text-blue-100">Go Live & Support</div>
                </div>
              </div>
              <p className="text-blue-100 text-sm mt-6">
                Full platform deployment with comprehensive training and ongoing support.
                <br />
                Implementation timeline depends on community size and organizational readiness.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Emergency Contact */}
        <div className="text-center mt-12">
          <p className="text-blue-200 text-sm mb-2">
            Need immediate assistance or have urgent questions?
          </p>
          {/* <a 
            href="mailto:support@example.com" 
            className="text-white font-semibold hover:text-blue-200 transition-colors"
          >
            Email support@example.com
          </a> */}
        </div>
      </div>

      {/* Decorative Background Elements */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-linear-to-t from-gray-50 to-transparent opacity-20"></div>
    </div>
  );
};
